import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FilterSpec, Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';
import type { ProductTaxDTO } from './product-tax.service';

export interface ProductDTO {
  id: number;
  name: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice: number;
  currency?: CurrencyDTO;
  measureUnitId?: number;
  measureUnitCode?: string;
  measureUnitName?: string;
  taxCodeId?: number;
  taxCodeTaxCode?: string;
  taxCodeDescription?: string;
  satObjetoImpId?: string;
  satObjetoImpName?: string;
  taxInclusive?: boolean;
  taxability?: string;
  state: 'ACTIVE' | 'INACTIVE';
  metadata?: Record<string, unknown>;
  createdUserId?: number;
  lastUserId?: number;
  taxes?: ProductTaxDTO[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CurrencyDTO {
  id: number;
  name: string;
  code: string;
}

export interface ProductFilterSpec extends FilterSpec {}

export type WrapperPageableResponseProductDTO = WrapperPageableResponse<ProductDTO>;
export type WrapperResponseProductDTO = WrapperResponse<ProductDTO>;

export interface CreateProductRequest {
  name: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice: number;
  currencyId: number;
  measureUnitId?: number;
  taxCodeId?: number;
  satObjetoImpId?: string;
  taxInclusive?: boolean | null;
  productTaxIds?: number[];
  metadata?: Record<string, unknown>;
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  sku?: string;
  externalId?: string;
  unitPrice?: number;
  currencyId?: number;
  measureUnitId?: number;
  taxCodeId?: number;
  satObjetoImpId?: string;
  taxInclusive?: boolean | null;
  productTaxIds?: number[];
  state?: 'ACTIVE' | 'INACTIVE';
  metadata?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getProducts(
    spec: ProductFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseProductDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            params = params.append(key, String(item));
          }
        });
        return;
      }
      params = params.append(key, String(value));
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseProductDTO>(
      `${this.apiUrl}/products`,
      { params }
    );
  }

  createProduct(request: CreateProductRequest): Observable<WrapperResponseProductDTO> {
    return this.http.post<WrapperResponseProductDTO>(`${this.apiUrl}/products`, request);
  }

  getProductById(productId: number): Observable<WrapperResponseProductDTO> {
    return this.http.get<WrapperResponseProductDTO>(`${this.apiUrl}/products/${productId}`);
  }

  updateProduct(productId: number, request: UpdateProductRequest): Observable<WrapperResponseProductDTO> {
    return this.http.put<WrapperResponseProductDTO>(`${this.apiUrl}/products/${productId}`, request);
  }

  deleteProduct(productId: number): Observable<WrapperResponseProductDTO> {
    return this.http.delete<WrapperResponseProductDTO>(`${this.apiUrl}/products/${productId}`);
  }
}
